import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import {
  activateModule,
  fetchAllModules,
  fetchCustomEmails,
  sendEmailV3,
  updateReservationV3,
} from "../../shared/queries";
import { moduleId } from "../../shared/utils/config";
import { BaseSelectItem, ModalView, ReservationV3 } from "../../types";
import Button from "../Button";
import Checkbox from "../Checkbox";
import FormField from "../FormField";
import FormSectionTitle from "../FormSectionTitle";
import SelectInput from "../SelectInput";
import ReservationNoteList from "./ReservationNoteList";
import Modal from "../Modal";
import ModalActions from "../ModalAction";
import ModalCard from "../ModalCard";
import ModalSection from "../ModalSection";
import ModuleCard from "../module/ModuleCard";
import TextInput from "../TextInput";

type ReservationNotificationsProps = {
  reservation: ReservationV3;
  onSave: () => void;
};

export default function ReservationNotifications({
  reservation,
  onSave,
}: ReservationNotificationsProps) {
  const { t } = useTranslation();
  const isFirstRun = useRef<boolean>(true);
  const [data, setData] = useState<ReservationV3>(reservation);
  const confirmationKey = t("general.iConfirm");
  const [sendStatusEmail, setSendStatusEmail] = useState("");
  const [messagingActive,setMessagingActive] = useState(false);
  const [messagingSMSActive,setMessagingSMSActive] = useState(false);
  const [messagingWhatsappActive,setMessagingWhatsappActive] = useState(false);
  const [messagingWhatsappOpening,setMessagingWhatsappOpening] = useState(false);
  const [messagingWhatsappComplete,setMessaggingWhatsappComplete] = useState(false);
  const [whatsappNotifSubMenu, setWhatsappNotifSubMenu] = useState(
    data.resv_wapp_notify_1days ||
      data.resv_wapp_notify_3days ||
      data.resv_wapp_notify_7days ||
      data.resv_wapp_notify_checkout ||
      data.resv_wapp_notify_now ||
      data.chk_whatsapp_done
  );
  const [modalView, setModalView] = useState<ModalView>({
    id: null,
    confirmValue: "",
    active: false,
    price: 0,
    defaultPrice: 0,
    priceCycles: 999,
  });
  const mutation = useMutation(
    () =>
      updateReservationV3({
        chk_cc: data.chk_cc,
        notify_mail_guest_active: data.notify_mail_guest_active,
        chk_mail: data.chk_mail,
        chk_sms: data.chk_sms,
        chk_whatsapp: data.chk_whatsapp,
        chk_sms_done: data.chk_sms_done,
        chk_whatsapp_done: data.chk_whatsapp_done,
        resv_key: data.resv_key,
        guest_email: reservation.guest_email,
        notify_now: data.notify_now,
        notify_1days: data.notify_1days,
        notify_checkout: data.notify_checkout,
        notify_3days: data.notify_3days,
        notify_7days: data.notify_7days,
        notify_done_1days: data.notify_done_1days,
        cust_email_ptr: data.cust_email_ptr,
        resv_wapp_notify_now: data.resv_wapp_notify_now,
        resv_wapp_notify_7days: data.resv_wapp_notify_7days,
        resv_wapp_notify_3days: data.resv_wapp_notify_3days,
        resv_wapp_notify_1days: data.resv_wapp_notify_1days,
        resv_wapp_notify_checkout: data.resv_wapp_notify_checkout,
      }),
    {
      onSuccess: () => {
        console.log("success");
        toast.success(t("general.updated"));
        if (onSave) onSave();
      },
      onError: () => {
        console.log("error");
        toast.error(t("general.requestError"));
      },
    }
  );

  useEffect(() => {
    if (isFirstRun.current === true) {
      isFirstRun.current = false;
      return;
    }
    if (!data) return;
    const timer = setTimeout(() => {
      mutation.mutate();
    }, 600);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const { data: emails, isLoading: isLoadingEmails } = useQuery(
    ["emails"],
    () => fetchCustomEmails(undefined, true)
  );

  function getEmailOptions() {
    return emails?.results
      ? [
          ...emails.results.map((c) => {
            return {
              label: c.objkey,
              value: c.id,
            };
          }),
          {
            label: "Vikey default",
            value: 0,
          },
        ]
      : [];
  }

  const mutateSendEmail = useMutation(
    () => sendEmailV3(sendStatusEmail, reservation?.resv_key ?? ""),
    {
      onSuccess: () => {
        toast.success(t("general.done"));
      },
    }
  );

  function sendEmailOptions() {
    return [
      { label: t("resv.F_TODO"), value: "F_TODO" },
      { label: t("resv.F_DONE"), value: "F_DONE" },
      {
        label: t("createCustomEmail.checkoutReservation"),
        value: "CHECKOUT_GUEST_REVIEW",
      },
      // { label: t("resv.DEPOSIT_GUEST_REMIND"), value: "DEPOSIT_GUEST_REMIND" },
    ];
  }

  const {
    data: dataModules,
    refetch: refetchDataModules,
    isFetching: isFetchingModules,
  } = useQuery<any>([], () =>
    fetchAllModules(reservation.local_key).then((data: any) => {
      console.log("all modules ", data);
      return data;
    })
  );
  const activateMutation = useMutation(
    (data: { local_keys: string[]; module_id: number }) =>
      activateModule(data.local_keys, data.module_id),
    {
      onSuccess: () => {
        window.dataLayer.push({
          event: "modulo_attivato_prenotazioni",
          gtm: {
            id_modulo: modalView.id,
            value: modalView.price
              ? modalView.price > 0
                ? (modalView.price / 100).toFixed(2)
                : (modalView.defaultPrice / 100).toFixed(2)
              : (modalView.defaultPrice / 100).toFixed(2),
          },
        });
        toast.success(t("general.requestCompleted"));
        refetchDataModules();
        setModalView({
          id: null,
          confirmValue: "",
          active: false,
          price: 0,
          defaultPrice: 0,
          priceCycles: 999,
        });
      },
      onError: (error: Error) => {
        toast.error(t("general.requestError"));
      },
    }
  );
  const openModalView = (idMod: number) => {
    console.log("setting open ", idMod);
    const data = dataModules.modules.find((mod: any) => mod.id === idMod);
    const price = data.self_price;
    const defaultPrice = data.default_price;
    window.dataLayer.push({
      event: "clic_attiva_modulo_prenotazioni",
      gtm: {
        id_modulo: idMod,
        location: data.results?.country_code?.toLowerCase(),
        value:
          price !== null
            ? price >= 0
              ? (price / 100).toFixed(2)
              : (defaultPrice / 100).toFixed(2)
            : (defaultPrice / 100).toFixed(2),
      },
    });

    setModalView((prev) => {
      return {
        ...prev,
        id: idMod,
        price: price,
        defaultPrice: defaultPrice,
        priceCycles: data.self_price_cycles,
      };
    });
    console.log("modal view ", modalView);
  };

  useEffect(() => {
    dataModules?.modules?.map((module: any) => {
      if (module.id === moduleId.sms && module.active) {
        setMessagingSMSActive(true);
      } else if (module.id === moduleId.whatsapp && module.active) {
        setMessagingWhatsappActive(true);
      } else if (module.id === moduleId.sms_whatsapp && module.active) {
        setMessagingSMSActive(true);
        setMessagingWhatsappActive(true);
      } else if (module.id === moduleId.whatsapp_complete && module.active) {
        setMessagingSMSActive(true);
        setMessagingWhatsappActive(true);
      } else {
        setMessagingSMSActive(false);
      }
      if(module.id === moduleId.sms_done && module.active){
        setMessagingActive(true)
      }
      else if (module.id === moduleId.whatsapp_done && module.active) {
        setMessagingWhatsappOpening(true);
      }else if (module.id === moduleId.sms_whatssapp_done && module.active) {
        setMessagingActive(true);
        setMessagingWhatsappOpening(true);
      } else if (module.id === moduleId.whatsapp_complete && module.active) {
        setMessagingActive(true);
        setMessagingWhatsappOpening(true);
      } else {
        setMessagingActive(false);
      }
      if (module.id === moduleId.whatsapp_complete && module.active) {
        setMessaggingWhatsappComplete(true);
      }else{
        setMessaggingWhatsappComplete(false);
      }
    });
  }, [dataModules]);

  return (
    <div className="md:text-sm text-xs">
      <FormSectionTitle title={t("resv.notificationSettings")} borderless />
      <div className="flex items-center space-x-2">
        <Checkbox
          label={`${t("resv.sendRemindSMS")}`}
          className={"mb-2 max-w-max"}
          hint={t("resv.twoDaySMSRemind")}
          disabled={!messagingSMSActive}
          onChange={() => {
            setData({
              ...data,
              chk_sms: !data.chk_sms,
            });
          }}
          checked={data.chk_sms || false}
        />

        {!messagingSMSActive && (
          <Button
            label={t("apartmentSettings.activeModule")}
            className="bg-activateModule"
            size="small"
            onClick={() => openModalView(moduleId.whatsapp_complete)}
          />
        )}
      </div>
      {!messagingWhatsappComplete && (
        <div className="flex items-center space-x-2">
          <Checkbox
            label={`${t("resv.sendRemindWHATSAPP")}`}
            className={"mb-2 max-w-max"}
            hint={t("resv.threeDayWHATSAPPRemind")}
            disabled={!messagingWhatsappActive}
            onChange={() => {
              setData({
                ...data,
                chk_whatsapp: !data.chk_whatsapp,
              });
            }}
            checked={data.chk_whatsapp || false}
          />
          {!messagingWhatsappActive && (
            <Button
              label={t("apartmentSettings.activeModule")}
              className="bg-activateModule"
              size="small"
              onClick={() => openModalView(moduleId.whatsapp_complete)}
            />
          )}
        </div>
      )}
      <div className="flex items-center space-x-2">
        <Checkbox
          label={`${t("resv.sendRemindSMSdone")}`}
          className={"mb-2 max-w-max"}
          disabled={!messagingActive}
          onChange={() => {
            setData({
              ...data,
              chk_sms_done: !data.chk_sms_done,
            });
          }}
          checked={data.chk_sms_done || false}
        />

        {!messagingActive && (
          <Button
            label={t("apartmentSettings.activeModule")}
            className="bg-activateModule"
            size="small"
            onClick={() => openModalView(moduleId.whatsapp_complete)}
          />
        )}
      </div>
      {!messagingWhatsappComplete && (
        <div className="flex items-center space-x-2">
          <Checkbox
            label={`${t("resv.sendRemindWHATSAPPdone")}`}
            className={"mb-2 max-w-max"}
            disabled={!messagingWhatsappOpening}
            onChange={() => {
              setData({
                ...data,
                chk_whatsapp_done: !data.chk_whatsapp_done,
              });
            }}
            checked={data.chk_whatsapp_done || false}
          />
          {!messagingWhatsappOpening && (
            <Button
              label={t("apartmentSettings.activeModule")}
              className="bg-activateModule"
              size="small"
              onClick={() => openModalView(moduleId.whatsapp_complete)}
            />
          )}
        </div>
      )}

      <Checkbox
        checked={data.chk_mail || false}
        className={"mb-2 max-w-max"}
        label={t("resv.enableEmails")}
        hint={t("resv.enableEmailsText")}
        onChange={() => {
          if (data.chk_mail) {
            setData({
              ...data,
              notify_1days: false,
              notify_3days: false,
              notify_7days: false,
              notify_now: false,
              notify_checkout: false,
              notify_done_1days: false,
              chk_mail: false,
            });
          } else {
            setData({
              ...data,
              chk_mail: !data.chk_mail,
            });
          }
        }}
      />

      {data.chk_mail && (
        <div className="space-y-4 ml-10 mt-4 mb-4 p-4 border border-gray-200 rounded-2xl bg-white">
          <FormField label={t("resv.emailToBeSent")}>
            <SelectInput<BaseSelectItem, false>
              value={getEmailOptions().find(
                (e) => e.value === data.cust_email_ptr
              )}
              onChange={(item) => {
                if (item) {
                  setData({
                    ...data,
                    cust_email_ptr: item.value,
                  });
                }
              }}
              placeholder={t("resv.chooseEmail")}
              isLoading={isLoadingEmails}
              options={getEmailOptions()}
            />
          </FormField>
          <Checkbox
            label={t("resv.createNotification")}
            checked={data.notify_now}
            onChange={() =>
              setData({
                ...data,
                notify_now: !data.notify_now,
              })
            }
          />

          <Checkbox
            label={t("resv.oneDayNotification")}
            checked={data.notify_1days}
            onChange={() =>
              setData({
                ...data,
                notify_1days: !data.notify_1days,
              })
            }
          />

          <Checkbox
            label={t("resv.threeDayNotification")}
            checked={data.notify_3days}
            onChange={() =>
              setData({
                ...data,
                notify_3days: !data.notify_3days,
              })
            }
          />

          <Checkbox
            label={t("resv.sevenDayNotification")}
            checked={data.notify_7days}
            onChange={() =>
              setData({
                ...data,
                notify_7days: !data.notify_7days,
              })
            }
          />

          <Checkbox
            label={t("integration.completeNotification")}
            checked={data.notify_done_1days}
            onChange={() =>
              setData({
                ...data,
                notify_done_1days: !data.notify_done_1days,
              })
            }
          />

          <Checkbox
            label={t("resv.checkoutNotification")}
            checked={data.notify_checkout}
            onChange={() =>
              setData({
                ...data,
                notify_checkout: !data.notify_checkout,
              })
            }
          />
        </div>
      )}
      <div className="flex justify-start mt-3 gap-5">
        <Checkbox
          checked={whatsappNotifSubMenu}
          className="mb-2 max-w-max"
          label={t("resv.enableResvWappNotifs")}
          hint={t("resv.enableResvNotifsText")}
          onChange={() => {
            if (whatsappNotifSubMenu) {
              setData({
                ...data,
                resv_wapp_notify_1days: false,
                resv_wapp_notify_3days: false,
                resv_wapp_notify_7days: false,
                resv_wapp_notify_now: false,
                resv_wapp_notify_checkout: false,
                chk_whatsapp_done: false,
              });
            }
            setWhatsappNotifSubMenu(!whatsappNotifSubMenu);
          }}
          disabled={
            !dataModules?.modules ||
            !dataModules.modules.some(
              (mod: any) => mod.id === moduleId.whatsapp_complete
            ) ||
            !dataModules.modules.some(
              (mod: any) =>
                mod.id === moduleId.whatsapp_complete && mod.self_active
            )
          }
        />
        {!dataModules?.modules?.some(
          (module: any) =>
            module.id === moduleId.whatsapp_complete && module.active
        ) && (
          <Button
            label={t("apartmentSettings.activeModule")}
            className="bg-activateModule h-1/2 place-self-center"
            size="small"
            onClick={() => openModalView(moduleId.whatsapp_complete)}
          />
        )}
      </div>

      {whatsappNotifSubMenu &&
        dataModules?.modules?.some(
          (module: any) =>
            module.id === moduleId.whatsapp_complete && module.self_active > 0
        ) && (
          <div className="space-y-4 ml-10 mt-4 mb-4 p-4 border border-gray-200 rounded-2xl bg-white">
            <Checkbox
              label={t("resv.createNotification")}
              checked={data.resv_wapp_notify_now}
              onChange={() =>
                setData({
                  ...data,
                  resv_wapp_notify_now: !data.resv_wapp_notify_now,
                })
              }
            />

            <Checkbox
              label={t("resv.oneDayNotification")}
              checked={data.resv_wapp_notify_1days}
              onChange={() =>
                setData({
                  ...data,
                  resv_wapp_notify_1days: !data.resv_wapp_notify_1days,
                })
              }
            />

            <Checkbox
              label={t("resv.threeDayNotification")}
              checked={data.resv_wapp_notify_3days}
              onChange={() =>
                setData({
                  ...data,
                  resv_wapp_notify_3days: !data.resv_wapp_notify_3days,
                })
              }
            />

            <Checkbox
              label={t("resv.sevenDayNotification")}
              checked={data.resv_wapp_notify_7days}
              onChange={() =>
                setData({
                  ...data,
                  resv_wapp_notify_7days: !data.resv_wapp_notify_7days,
                })
              }
            />
            {dataModules?.modules?.map((module: any) => {
              if (module.id === moduleId.whatsapp_complete) {
                return (
                  <Checkbox
                    label={`${t("resv.sendRemindWHATSAPPdone1day")} ${
                      module.date_closed
                        ? `(${t("resv.activeUntil").toLowerCase()} ${dayjs(
                            module.date_closed
                          ).format("DD-MM-YYYY")})`
                        : ""
                    }`}
                    className={"mb-2 max-w-max"}
                    disabled={!module.active}
                    onChange={() => {
                      setData({
                        ...data,
                        chk_whatsapp_done: !data.chk_whatsapp_done,
                      });
                    }}
                    checked={data.chk_whatsapp_done || false}
                  />
                );
              }
              return null;
            })}

            <Checkbox
              label={t("resv.wappCheckoutNotification")}
              checked={data.resv_wapp_notify_checkout}
              onChange={() =>
                setData({
                  ...data,
                  resv_wapp_notify_checkout: !data.resv_wapp_notify_checkout,
                })
              }
            />
          </div>
        )}

      <Checkbox
        checked={data.chk_cc || false}
        className={"mb-2 max-w-max"}
        label={t("resv.enableCCEmail")}
        hint={t("resv.enableCCEmailText")}
        onChange={() => {
          setData({
            ...data,
            chk_cc: !data.chk_cc,
          });
        }}
      />

      <Checkbox
        checked={data.notify_mail_guest_active || false}
        className={"mb-2 max-w-max"}
        label={t("resv.enableGuestActive")}
        onChange={() => {
          setData({
            ...data,
            notify_mail_guest_active: !data.notify_mail_guest_active,
          });
        }}
      />

      <FormSectionTitle title={t("resv.notificationSettings")} />

      <div className="space-y-4 mt-4 mb-4 p-4 border border-gray-200 rounded-2xl bg-white">
        <FormField label={t("resv.emailToBeSent")}>
          <SelectInput<BaseSelectItem, false>
            // value={sendEmailOptions().find(
            //   (e) => e.value === data.cust_email_ptr
            // )}
            onChange={(item) => {
              if (item) {
                setSendStatusEmail(item.value);
              }
            }}
            placeholder={t("resv.chooseEmail")}
            isLoading={isLoadingEmails}
            options={sendEmailOptions()}
          />
        </FormField>
        <Button
          icon="send"
          label={t("resv.send")}
          onClick={() => {
            mutateSendEmail.mutate();
          }}
        />
      </div>

      <div className={"w-full"}>
        <FormSectionTitle title={t("resv.notifsAndEvents")} />
        <ReservationNoteList reservation={reservation} />
      </div>
      <Modal visible={!!modalView.id}>
        <ModalCard
          title={`${t("apartmentSettings.activeModule").toUpperCase()} - ${t(
            `module.id${modalView.id}`
          ).toUpperCase()}`}
          className="w-full"
        >
          <ModalSection>
            <div className="text-gray-700 p-4 pt-0 border-gray-300">
              <ModuleCard
                moduleName={t(`module.id${modalView.id}`).toUpperCase()}
                active={modalView.active}
                price={modalView.price}
                defaultPrice={modalView.defaultPrice}
                onChange={() => {
                  setModalView((prev) => {
                    if (prev.active) {
                      return {
                        ...prev,
                        active: !prev.active,
                        confirmValue: "",
                      };
                    }
                    return {
                      ...prev,
                      active: !prev.active,
                    };
                  });
                }}
                priceCycles={modalView.priceCycles}
              />
            </div>
            {modalView.active && (
              <div className="text-gray-700 p-4 pt-0 border-gray-300">
                {t("general.write1")}
                <span className={"font-bold"}>{confirmationKey}</span>{" "}
                {t("general.write2")}
                <div className="mt-2">
                  <TextInput
                    value={modalView.confirmValue}
                    onChange={(val) =>
                      setModalView((prev) => {
                        return {
                          ...prev,
                          confirmValue: val,
                        };
                      })
                    }
                  />
                </div>
              </div>
            )}
          </ModalSection>
          <ModalActions
            isLoading={activateMutation.isLoading}
            onSave={() => {
              if (
                modalView.confirmValue === confirmationKey &&
                reservation.local &&
                reservation.local.local_key
              ) {
                activateMutation.mutate({
                  local_keys: [reservation.local.local_key],
                  module_id: modalView.id as number,
                });
              }
            }}
            saveLabel={t("general.enable")}
            saveDisabled={modalView.confirmValue !== confirmationKey}
            onClose={() => {
              setModalView({
                id: null,
                confirmValue: "",
                active: false,
                price: 0,
                defaultPrice: 0,
                priceCycles: 999,
              });
            }}
          />
        </ModalCard>
      </Modal>
    </div>
  );
}
